import { Dispatch, SetStateAction, useEffect, useState } from 'react';

type usePageExitPrompt = [boolean, Dispatch<SetStateAction<boolean>>];

/**
 * Initializes a beforeunload event handler to prompt the user when attempting to leave the page.
 * @param {boolean} showExitPrompt - Determines whether to show the exit prompt.
 * @param {string} promptMessage - The message to display in the exit prompt.
 * @returns {void}
 */
const initBeforeUnload = (showExitPrompt: boolean, promptMessage: string, Callback?: () => void) => {
	return function(event: BeforeUnloadEvent) {
		if (Callback) setTimeout(Callback, 500);
		// Show prompt based on state
		if (showExitPrompt) {
			const e = event;
			if (e) {
				e.preventDefault();
				e.returnValue = promptMessage;
			}
			return promptMessage;
		}
	};
};

/**
 * Todo(BM): research required for custom alert message
 * Hook to display a prompt when the user tries to leave/Navigate the page.
 * @param {boolean} state - The state indicating whether to display the exit prompt.
 * @param {string} promptMessage - The message to display in the exit prompt.
 * @returns {[boolean, React.Dispatch<SetStateAction<boolean>>]} - A tuple containing the current state
 * whether to show the prompt and a function to update this state.
 */
const usePageExitPrompt = (state: boolean, promptMessage: string, Callback?: () => void): usePageExitPrompt => {
	const [showExitPrompt, setShowExitPrompt] = useState<boolean>(state);

	useEffect(() => {
		const tabBlocker = initBeforeUnload(showExitPrompt, promptMessage, Callback);

		window.addEventListener('beforeunload', tabBlocker);

		return () => window.removeEventListener('beforeunload', tabBlocker);
	}, [showExitPrompt, promptMessage]);

	return [showExitPrompt, setShowExitPrompt];
};

export default usePageExitPrompt;
